import React, { useState } from 'react';
import ButtonV2 from 'components/ButtonV2';
import { DeleteConfirmModal } from 'components/FormModal';
import TopicIcons from '../../LearningHub/components/TopicIcons';
import TopicFormModal from '../components/TopicFormModal';
import {
  useGetAdminTopics,
  useCreateTopic,
  useUpdateTopic,
  useDeleteTopic,
} from '../gql';
import {
  PageHeader,
  PageTitle,
  PageDescription,
  TabContainer,
  Grid,
  TopicCard,
  TopicHeader,
  TopicIcon,
  TopicTitle,
  TopicDescription,
  TopicFooter,
  StatusBadge,
  ActionButton,
} from '../StyledComponents';

const TopicsPage = () => {
  const [modeFilter, setModeFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editingTopic, setEditingTopic] = useState(null);
  const [deletingTopic, setDeletingTopic] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const mode = modeFilter !== 'all' ? modeFilter : undefined;
  const { topics } = useGetAdminTopics(mode);
  const createTopic = useCreateTopic();
  const updateTopic = useUpdateTopic();
  const deleteTopic = useDeleteTopic();

  const handleSave = async (data) => {
    const input = {
      name: data.name,
      slug: data.slug,
      description: data.description,
      mode: data.mode,
      icon: data.icon,
    };

    if (editingTopic) {
      await updateTopic({ id: editingTopic.id, ...input });
    } else {
      await createTopic(input);
    }
    setShowModal(false);
    setEditingTopic(null);
  };

  const handleEdit = (topic) => {
    setEditingTopic(topic);
    setShowModal(true);
  };

  const handleAdd = () => {
    setEditingTopic(null);
    setShowModal(true);
  };

  const handleDeleteClick = (topic) => {
    setDeletingTopic(topic);
  };

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    await deleteTopic(deletingTopic.id);
    setIsDeleting(false);
    setDeletingTopic(null);
  };

  return (
    <>
      <PageHeader>
        <div>
          <PageTitle>Topics</PageTitle>
          <PageDescription>Organize videos into categories</PageDescription>
        </div>
        <div style={{ minWidth: '160px' }}>
          <ButtonV2 onClick={handleAdd}>Add Topic</ButtonV2>
        </div>
      </PageHeader>

      <TabContainer
        style={{
          marginBottom: '24px',
          padding: '12px',
          background: 'rgba(0,0,0,0.02)',
          borderRadius: '12px',
        }}
      >
        <div style={{ display: 'flex', gap: '8px' }}>
          <ButtonV2
            variant="pill"
            active={modeFilter === 'all'}
            onClick={() => setModeFilter('all')}
          >
            All
          </ButtonV2>
          <ButtonV2
            variant="pill"
            active={modeFilter === 'discover'}
            onClick={() => setModeFilter('discover')}
          >
            Ideas & Tips
          </ButtonV2>
          <ButtonV2
            variant="pill"
            active={modeFilter === 'howto'}
            onClick={() => setModeFilter('howto')}
          >
            How To Do It
          </ButtonV2>
        </div>
      </TabContainer>

      <Grid>
        {topics.map((topic) => (
          <TopicCard key={topic.id}>
            <TopicHeader>
              <TopicIcon>
                <TopicIcons icon={topic.icon} />
              </TopicIcon>
              <StatusBadge variant={topic.mode}>
                {topic.mode === 'discover' ? 'Ideas & Tips' : 'How To Do It'}
              </StatusBadge>
            </TopicHeader>
            <TopicTitle>{topic.name}</TopicTitle>
            <TopicDescription>{topic.description}</TopicDescription>
            <TopicFooter>
              <span
                style={{ fontSize: '14px', color: 'var(--text-secondary)' }}
              >
                {topic.video_count || 0} videos
              </span>
              <div style={{ display: 'flex', gap: '8px' }}>
                <ActionButton onClick={() => handleEdit(topic)}>
                  <svg
                    width="16"
                    height="16"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                    />
                  </svg>
                </ActionButton>
                <ActionButton danger onClick={() => handleDeleteClick(topic)}>
                  <svg
                    width="16"
                    height="16"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                    />
                  </svg>
                </ActionButton>
              </div>
            </TopicFooter>
          </TopicCard>
        ))}
      </Grid>

      {showModal && (
        <TopicFormModal
          topic={editingTopic}
          onSave={handleSave}
          onClose={() => {
            setShowModal(false);
            setEditingTopic(null);
          }}
        />
      )}

      <DeleteConfirmModal
        isOpen={!!deletingTopic}
        onClose={() => setDeletingTopic(null)}
        title="Delete Topic?"
        message={`Are you sure you want to delete ${deletingTopic?.name}? This action cannot be undone.`}
        onConfirm={handleDeleteConfirm}
        isDeleting={isDeleting}
      />
    </>
  );
};

export default TopicsPage;
